import type { Aabb, District, PropertyPublic, WorldManifest } from "@townsy/contracts";
import { WORLD_STREETS, type StreetInfo } from "@townsy/world";

/**
 * The small town map (ADR-0008 #3: "a small minimap helps orientation" in the bigger v3 city).
 * Everything here is pure: the projection between world metres and canvas pixels, the street
 * label layout, hit testing for clicks, the plot colouring rule and the per-viewer on/off
 * preference (Web Storage may be missing or throw: private windows, blocked site data).
 *
 * World v5 (ADR-0041 decision 9): two views. "Here" is the district the visitor is standing in (its streets and plots);
 * "Town" fits all three districts side by side (one shared world frame) with their names and "you are here" on the
 * current one. ADR-0042 (one continuous town): the districts are joined by road, not gates. The roads that cross a
 * district line come from the manifest's road surfaces and named streets (`districtRoads`): Here draws each one
 * running off the map's edge, labelled with the district it leads to; Town draws them over the district outlines.
 *
 * Conventions: world x grows east (right on the map), world z grows north (UP on the map, so
 * canvas y = (maxZ - z) * scale); a yaw of 0 faces north (+z) and points up on the map.
 */

export interface MinimapView {
  /** Canvas size in CSS pixels. */
  width: number;
  height: number;
  /** Pixels per metre (the same on both axes: the map is never stretched). */
  scale: number;
  /** Pixel position of the world rect's west / north edge. */
  offsetX: number;
  offsetY: number;
  world: Aabb;
}

export interface MapPoint {
  x: number;
  y: number;
}

export interface WorldPoint {
  x: number;
  z: number;
}

/** Fit `world` into a width x height canvas with `padding` pixels around it, centred, aspect kept. */
export function fitMinimap(world: Aabb, width: number, height: number, padding = 4): MinimapView {
  const ww = Math.max(1e-6, world.maxX - world.minX);
  const wd = Math.max(1e-6, world.maxZ - world.minZ);
  const innerW = Math.max(1, width - padding * 2);
  const innerH = Math.max(1, height - padding * 2);
  const scale = Math.min(innerW / ww, innerH / wd);
  return {
    width,
    height,
    scale,
    offsetX: (width - ww * scale) / 2,
    offsetY: (height - wd * scale) / 2,
    world,
  };
}

/** Canvas height (CSS px) that shows `world` at `width` with no empty bands (plus the padding). */
export function minimapHeightFor(world: Aabb, width: number, padding = 4): number {
  const ww = Math.max(1e-6, world.maxX - world.minX);
  const wd = Math.max(1e-6, world.maxZ - world.minZ);
  return Math.round(((width - padding * 2) * wd) / ww + padding * 2);
}

/**
 * The canvas height the map uses: the district's shape (minimapHeightFor), but never taller than 150 px on the
 * narrower phone map (< 280 px wide) or 240 px on the desktop one, so Riverside and Hillcrest (squarer than Central)
 * do not cover more of the town; a capped map keeps its aspect with bands at the sides.
 */
export function minimapCanvasHeight(world: Aabb, width: number, padding = 4): number {
  return Math.min(minimapHeightFor(world, width, padding), width < 280 ? 150 : 240);
}

// ---------------------------------------------------------------------------------------------
// Views: Here (the current district) and Town (every district)

export type MinimapViewKind = "here" | "town";

/** The rectangle around every district: the Town view (v5: x -632..632, z -178..150). */
export function townBounds(manifest: Pick<WorldManifest, "districts">): Aabb {
  const ds = manifest.districts;
  return {
    minX: Math.min(...ds.map((d) => d.bounds.minX)),
    minZ: Math.min(...ds.map((d) => d.bounds.minZ)),
    maxX: Math.max(...ds.map((d) => d.bounds.maxX)),
    maxZ: Math.max(...ds.map((d) => d.bounds.maxZ)),
  };
}

/** The district a view is about: the one with `districtId`, else the manifest's first (Central). */
export function minimapDistrict(manifest: Pick<WorldManifest, "districts">, districtId: string | null | undefined): District {
  return manifest.districts.find((d) => d.id === districtId) ?? manifest.districts[0]!;
}

/** What a view frames: Here = the current district's bounds, Town = every district. */
export function minimapWorld(kind: MinimapViewKind, manifest: Pick<WorldManifest, "districts">, districtId: string | null | undefined): Aabb {
  return kind === "town" ? townBounds(manifest) : minimapDistrict(manifest, districtId).bounds;
}

export function worldToMinimap(v: MinimapView, x: number, z: number): MapPoint {
  return { x: v.offsetX + (x - v.world.minX) * v.scale, y: v.offsetY + (v.world.maxZ - z) * v.scale };
}

export function minimapToWorld(v: MinimapView, px: number, py: number): WorldPoint {
  return { x: v.world.minX + (px - v.offsetX) / v.scale, z: v.world.maxZ - (py - v.offsetY) / v.scale };
}

/** A world rectangle as a canvas rectangle (top-left corner + size). */
export function rectToMinimap(v: MinimapView, r: Aabb): { x: number; y: number; w: number; h: number } {
  const tl = worldToMinimap(v, r.minX, r.maxZ);
  return { x: tl.x, y: tl.y, w: (r.maxX - r.minX) * v.scale, h: (r.maxZ - r.minZ) * v.scale };
}

/**
 * Canvas rotation for a world yaw: a marker drawn pointing UP (-y) and rotated by this angle points
 * the way the yaw faces. Canvas rotations are clockwise on screen, exactly like yaw (north -> east).
 */
export function yawToCanvasAngle(yaw: number): number {
  return Number.isFinite(yaw) ? yaw : 0;
}

/** Unit direction on the canvas for a world yaw (yaw 0 = up, PI/2 = right). */
export function headingOnMap(yaw: number): { dx: number; dy: number } {
  const a = yawToCanvasAngle(yaw);
  return { dx: Math.sin(a), dy: -Math.cos(a) };
}

// ---------------------------------------------------------------------------------------------
// Plots

export type MinimapPlotState = "owned" | "available" | "civic";

export interface MinimapPlot {
  id: string;
  type: "COMMERCIAL" | "RESIDENTIAL" | "CIVIC";
  address: string;
  bounds: Aabb;
  /** World v5: the plot's district (the Town view names it when hovering another district's place). */
  districtId: string;
}

/** The plots of one district (Here), or of the whole town when `districtId` is omitted (Town). */
export function minimapPlots(manifest: Pick<WorldManifest, "plots">, districtId?: string): MinimapPlot[] {
  return manifest.plots
    .filter((p) => !districtId || p.districtId === districtId)
    .map((p) => ({ id: p.id, type: p.type, address: p.address, bounds: p.bounds, districtId: p.districtId }));
}

/**
 * How a plot is coloured: Townsy-run buildings are "civic"; a property the public list reports as
 * AVAILABLE (an unowned store or a lot for sale) is "available"; everything else is "owned"
 * (owned, demo-held or suspended). An unknown property (list not loaded yet) is drawn as owned so
 * the map never advertises a sale the server has not reported.
 */
export function minimapPlotState(type: MinimapPlot["type"], property: Pick<PropertyPublic, "status"> | null | undefined): MinimapPlotState {
  if (type === "CIVIC") return "civic";
  return property?.status === "AVAILABLE" ? "available" : "owned";
}

/**
 * The plot under a world point, or null. `toleranceMetres` widens every plot (small targets on a
 * small map); when several match, the one whose centre is nearest wins.
 */
export function hitTestPlot(plots: readonly MinimapPlot[], point: WorldPoint, toleranceMetres = 0): MinimapPlot | null {
  let best: MinimapPlot | null = null;
  let bestD = Infinity;
  for (const p of plots) {
    const b = p.bounds;
    if (point.x < b.minX - toleranceMetres || point.x > b.maxX + toleranceMetres) continue;
    if (point.z < b.minZ - toleranceMetres || point.z > b.maxZ + toleranceMetres) continue;
    const d = Math.hypot(point.x - (b.minX + b.maxX) / 2, point.z - (b.minZ + b.maxZ) / 2);
    if (d < bestD) {
      best = p;
      bestD = d;
    }
  }
  return best;
}

// ---------------------------------------------------------------------------------------------
// Street labels

export interface StreetLabel {
  streetId: string;
  name: string;
  /** Label centre (canvas px). */
  x: number;
  y: number;
  /** 0 = horizontal; -PI/2 = vertical, reading bottom to top. */
  angle: number;
  /** Axis-aligned hit rectangle around the drawn text (canvas px). */
  rect: { x: number; y: number; w: number; h: number };
}

/** Rough text width when no canvas is available to measure (tests, server rendering). */
export function approxTextWidth(text: string, fontPx: number): number {
  return text.length * fontPx * 0.56;
}

/**
 * Where a street's name goes: lanes between Main Street and Orchard Road; Orchard Road in its middle;
 * Main Street a third of the way from its west end (between the lanes, clear of the plaza where
 * visitors arrive, so the name never hides the "you" marker at spawn). World v4: the avenues halfway
 * between Main Street and Market Street; Market Street a third of the way from its west end, between
 * the casino and the Downs (clear of the crosswalks in front of the attractions).
 */
function labelAnchor(s: StreetInfo, backRoadZ: number | null): WorldPoint {
  if (s.kind === "lane") {
    const top = s.from.z;
    const bottom = backRoadZ !== null && backRoadZ < top && backRoadZ > s.to.z ? backRoadZ : s.to.z;
    return { x: s.from.x, z: (top + bottom) / 2 };
  }
  const t = s.kind === "main" ? 0.29 : s.kind === "street" ? 0.27 : 0.5;
  return { x: s.from.x + (s.to.x - s.from.x) * t, z: s.from.z + (s.to.z - s.from.z) * t };
}

/** Streets drawn with a vertical name (they run north-south). */
const isVertical = (s: StreetInfo) => s.kind === "lane" || s.kind === "avenue";

export function layoutStreetLabels(
  v: MinimapView,
  streets: readonly StreetInfo[] = WORLD_STREETS,
  measure: (text: string) => number = (t) => approxTextWidth(t, 10),
  fontPx = 10,
): StreetLabel[] {
  const back = streets.find((s) => s.kind === "back") ?? null;
  const backZ = back ? back.from.z : null;
  return streets.map((s) => {
    const anchor = labelAnchor(s, backZ);
    const c = worldToMinimap(v, anchor.x, anchor.z);
    const vertical = isVertical(s);
    const w = measure(s.name) + 6;
    const h = fontPx + 4;
    const rect = vertical ? { x: c.x - h / 2, y: c.y - w / 2, w: h, h: w } : { x: c.x - w / 2, y: c.y - h / 2, w, h };
    return { streetId: s.id, name: s.name, x: c.x, y: c.y, angle: vertical ? -Math.PI / 2 : 0, rect };
  });
}

export function hitTestLabel(labels: readonly StreetLabel[], p: MapPoint): StreetLabel | null {
  for (const l of labels) {
    const r = l.rect;
    if (p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h) return l;
  }
  return null;
}

/**
 * What a point on the map means: a street name first (drawn on top), then a road to another district (Here view; it
 * only explains itself: walking or driving along it is how you get there), then a plot.
 */
export type MinimapHit =
  | { kind: "street"; name: string; streetId: string }
  | { kind: "road"; toDistrictId: string; name: string; streetName: string | null }
  | { kind: "plot"; id: string }
  | null;

export function hitTestMinimap(
  v: MinimapView,
  labels: readonly StreetLabel[],
  plots: readonly MinimapPlot[],
  p: MapPoint,
  tolerancePx = 3,
  links: readonly RoadLink[] = [],
): MinimapHit {
  const label = hitTestLabel(labels, p);
  if (label) return { kind: "street", name: label.name, streetId: label.streetId };
  const link = links.find((l) => inRect(l.rect, p));
  if (link) return { kind: "road", toDistrictId: link.toDistrictId, name: link.name, streetName: link.streetName };
  const plot = hitTestPlot(plots, minimapToWorld(v, p.x, p.y), tolerancePx / Math.max(v.scale, 1e-6));
  return plot ? { kind: "plot", id: plot.id } : null;
}

const inRect = (r: { x: number; y: number; w: number; h: number }, p: MapPoint) => p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;

// ---------------------------------------------------------------------------------------------
// Roads between districts (ADR-0042 decision 2: Riverside and Hillcrest are attached to Central by road)

/** How far (metres) a road may stop short of a district line and still count as crossing it (rounding, kerbs). */
export const DISTRICT_ROAD_TOLERANCE = 0.5;
/** How far (metres) from the crossing the named street on each side is looked for ("Market Street", "River Street"). */
const STREET_NAME_REACH = 80;

/** A road that crosses the line between two neighbouring districts. */
export interface DistrictRoad {
  /** The two districts it joins, in manifest order (Central before Riverside and Hillcrest). */
  districtIds: readonly [string, string];
  /** The district line it crosses: x = `at` between east and west neighbours, z = `at` between north and south ones. */
  axis: "x" | "z";
  at: number;
  /** The road's extent across the line (its z range on an x line, its x range on a z line), world metres. */
  span: { min: number; max: number };
  /** The road rectangles at the line, from every district (world metres; a road may be split at the line). */
  roads: readonly Aabb[];
  /** The named street the road is on in each district (by district id), or null where none is near. */
  streetNames: Readonly<Record<string, string | null>>;
}

/**
 * The named street a crossing road continues as: the nearest street piece in line with it (across an x line, a piece
 * whose z range overlaps the road's span), within STREET_NAME_REACH metres of the line; else null.
 */
function streetInLine(streets: readonly StreetInfo[], axis: "x" | "z", at: number, span: { min: number; max: number }): string | null {
  let best: string | null = null;
  let bestD = STREET_NAME_REACH;
  for (const s of streets) {
    for (const r of s.roads) {
      const [lo, hi, a, b] = axis === "x" ? [r.minZ, r.maxZ, r.minX, r.maxX] : [r.minX, r.maxX, r.minZ, r.maxZ];
      if (hi <= span.min || lo >= span.max) continue;
      const d = Math.max(a - at, 0, at - b);
      if (d <= bestD) {
        bestD = d;
        best = s.name;
      }
    }
  }
  return best;
}

/**
 * Every road that crosses the shared line of two neighbouring districts (their bounds touch), from the manifest's road
 * surfaces and each district's named streets (`streetsOf`). Rectangles at the same line whose spans overlap are one
 * road (a road split at the line, or a street and its surface). World v5 has none (the districts were walled, with
 * walk-only gates); world v6 (ADR-0042) has Market Street running east into River Street and west into High Street.
 */
export function districtRoads(
  manifest: Pick<WorldManifest, "districts" | "surfaces">,
  streetsOf: (districtId: string) => readonly StreetInfo[] = () => [],
  tolerance = DISTRICT_ROAD_TOLERANCE,
): DistrictRoad[] {
  const rects: Aabb[] = manifest.surfaces.filter((s) => s.kind === "road").map((s) => s.aabb);
  for (const d of manifest.districts) for (const s of streetsOf(d.id)) rects.push(...s.roads);
  const out: DistrictRoad[] = [];
  const ds = manifest.districts;
  for (let i = 0; i < ds.length; i++) {
    for (let j = i + 1; j < ds.length; j++) {
      const a = ds[i]!;
      const b = ds[j]!;
      for (const line of sharedLines(a.bounds, b.bounds)) {
        const crossing = rects.filter((r) =>
          line.axis === "x"
            ? r.minX <= line.at + tolerance && r.maxX >= line.at - tolerance && r.maxZ > line.min && r.minZ < line.max
            : r.minZ <= line.at + tolerance && r.maxZ >= line.at - tolerance && r.maxX > line.min && r.minX < line.max,
        );
        for (const group of groupBySpan(crossing, line.axis)) {
          const span = spanOf(group, line.axis);
          out.push({
            districtIds: [a.id, b.id],
            axis: line.axis,
            at: line.at,
            span,
            roads: group,
            streetNames: { [a.id]: streetInLine(streetsOf(a.id), line.axis, line.at, span), [b.id]: streetInLine(streetsOf(b.id), line.axis, line.at, span) },
          });
        }
      }
    }
  }
  return out;
}

/** The lines two district rectangles share (touching edges with an overlap), as {axis, at, min..max along it}. */
function sharedLines(a: Aabb, b: Aabb): { axis: "x" | "z"; at: number; min: number; max: number }[] {
  const eps = 1e-6;
  const lines: { axis: "x" | "z"; at: number; min: number; max: number }[] = [];
  const zMin = Math.max(a.minZ, b.minZ);
  const zMax = Math.min(a.maxZ, b.maxZ);
  if (zMax > zMin) {
    if (Math.abs(a.maxX - b.minX) < eps) lines.push({ axis: "x", at: a.maxX, min: zMin, max: zMax });
    if (Math.abs(b.maxX - a.minX) < eps) lines.push({ axis: "x", at: a.minX, min: zMin, max: zMax });
  }
  const xMin = Math.max(a.minX, b.minX);
  const xMax = Math.min(a.maxX, b.maxX);
  if (xMax > xMin) {
    if (Math.abs(a.maxZ - b.minZ) < eps) lines.push({ axis: "z", at: a.maxZ, min: xMin, max: xMax });
    if (Math.abs(b.maxZ - a.minZ) < eps) lines.push({ axis: "z", at: a.minZ, min: xMin, max: xMax });
  }
  return lines;
}

function spanOf(rects: readonly Aabb[], axis: "x" | "z"): { min: number; max: number } {
  return axis === "x"
    ? { min: Math.min(...rects.map((r) => r.minZ)), max: Math.max(...rects.map((r) => r.maxZ)) }
    : { min: Math.min(...rects.map((r) => r.minX)), max: Math.max(...rects.map((r) => r.maxX)) };
}

/** Rectangles whose spans across the line overlap (or touch) belong to one road; duplicates are dropped. */
function groupBySpan(rects: readonly Aabb[], axis: "x" | "z"): Aabb[][] {
  const lo = (r: Aabb) => (axis === "x" ? r.minZ : r.minX);
  const hi = (r: Aabb) => (axis === "x" ? r.maxZ : r.maxX);
  const unique = rects.filter((r, i) => rects.findIndex((q) => q.minX === r.minX && q.minZ === r.minZ && q.maxX === r.maxX && q.maxZ === r.maxZ) === i);
  const sorted = [...unique].sort((p, q) => lo(p) - lo(q));
  const groups: Aabb[][] = [];
  let end = -Infinity;
  for (const r of sorted) {
    if (groups.length > 0 && lo(r) <= end) {
      groups[groups.length - 1]!.push(r);
      end = Math.max(end, hi(r));
    } else {
      groups.push([r]);
      end = hi(r);
    }
  }
  return groups;
}

/** The roads that join `districtId` to another district, each with the district it leads to. */
export function roadsFrom(roads: readonly DistrictRoad[], districtId: string): (DistrictRoad & { toDistrictId: string })[] {
  return roads
    .filter((r) => r.districtIds.includes(districtId))
    .map((r) => ({ ...r, toDistrictId: r.districtIds[0] === districtId ? r.districtIds[1] : r.districtIds[0] }));
}

/** A road to another district on the Here view: drawn from the district line to the map's edge, with a label. */
export interface RoadLink {
  toDistrictId: string;
  /** "Riverside": the district it leads to (the label reads "To Riverside"). */
  name: string;
  /** The street it becomes over there ("River Street"), or null. */
  streetName: string | null;
  /** Which edge of the map it leaves by. */
  side: "east" | "west" | "north" | "south";
  /** The road's own rectangles at the line (world metres; drawn too, so the road reaches the district's streets). */
  roads: readonly Aabb[];
  /** The piece of road from the district line to the canvas edge (canvas px, at least 3 px wide). */
  stub: { x: number; y: number; w: number; h: number };
  /** Label anchor: text baseline-centred on y, starting (left) or ending (right) at x. */
  label: { x: number; y: number; align: "left" | "right"; text: string };
  /** Hit rectangle around the road's end and its label (hover explains where it goes). */
  rect: { x: number; y: number; w: number; h: number };
}

/** "To Riverside", the label on a road leaving the Here map. */
export const roadLinkLabel = (districtName: string) => `To ${districtName}`;

/**
 * The Here view's roads out of `district` (ADR-0042: no gates; the town is joined by road). Each road's end runs from
 * the district line to the canvas edge, so it visibly leaves the map, and the destination's name sits just above an
 * east or west road (beside a north or south one), reading inward from the edge.
 */
export function layoutRoadLinks(
  v: MinimapView,
  district: Pick<District, "id" | "bounds">,
  roads: readonly DistrictRoad[],
  nameOf: (districtId: string) => string,
  measure: (text: string) => number = (t) => approxTextWidth(t, 10),
  fontPx = 10,
): RoadLink[] {
  const b = district.bounds;
  return roadsFrom(roads, district.id).flatMap((road): RoadLink[] => {
    const side: RoadLink["side"] | null =
      road.axis === "x" ? (road.at === b.maxX ? "east" : road.at === b.minX ? "west" : null) : road.at === b.maxZ ? "north" : road.at === b.minZ ? "south" : null;
    if (!side) return [];
    const name = nameOf(road.toDistrictId);
    const text = roadLinkLabel(name);
    const w = measure(text);
    const h = fontPx + 4;
    let stub: RoadLink["stub"];
    let label: RoadLink["label"];
    if (road.axis === "x") {
      const lineX = worldToMinimap(v, road.at, 0).x;
      const top = worldToMinimap(v, road.at, road.span.max).y;
      const bottom = worldToMinimap(v, road.at, road.span.min).y;
      const th = Math.max(bottom - top, 3);
      const y = (top + bottom) / 2 - th / 2;
      const x0 = side === "east" ? Math.min(lineX, v.width - 6) : 0;
      const x1 = side === "east" ? v.width : Math.max(lineX, 6);
      stub = { x: x0, y, w: x1 - x0, h: th };
      // Above the road, from the edge inward: an east road's name ends at the edge, a west road's starts there.
      label = side === "east" ? { x: v.width - 2, y: y - fontPx * 0.75, align: "right", text } : { x: 2, y: y - fontPx * 0.75, align: "left", text };
    } else {
      const lineY = worldToMinimap(v, 0, road.at).y;
      const left = worldToMinimap(v, road.span.min, road.at).x;
      const right = worldToMinimap(v, road.span.max, road.at).x;
      const tw = Math.max(right - left, 3);
      const x = (left + right) / 2 - tw / 2;
      const y0 = side === "south" ? Math.min(lineY, v.height - 6) : 0;
      const y1 = side === "south" ? v.height : Math.max(lineY, 6);
      stub = { x, y: y0, w: tw, h: y1 - y0 };
      const fitsRight = x + tw + 4 + w <= v.width - 2;
      const ly = side === "south" ? v.height - h / 2 - 2 : h / 2 + 2;
      label = fitsRight ? { x: x + tw + 4, y: ly, align: "left", text } : { x: x - 4, y: ly, align: "right", text };
    }
    const lx = label.align === "left" ? label.x : label.x - w;
    const minX = Math.max(Math.min(stub.x, lx) - 3, 0);
    const maxX = Math.min(Math.max(stub.x + stub.w, lx + w) + 3, v.width);
    const minY = Math.max(Math.min(stub.y, label.y - h / 2) - 3, 0);
    const maxY = Math.min(Math.max(stub.y + stub.h, label.y + h / 2) + 3, v.height);
    return [
      {
        toDistrictId: road.toDistrictId,
        name,
        streetName: road.streetNames[road.toDistrictId] ?? null,
        side,
        roads: road.roads,
        stub,
        label,
        rect: { x: minX, y: minY, w: maxX - minX, h: maxY - minY },
      },
    ];
  });
}

/**
 * The map's screen-reader label. Here: "Map of Central, with roads to Riverside and Hillcrest. You: On Market Street,
 * Central. …"; Town: "Map of the whole town: Central, Riverside, Hillcrest, joined by road. You are in Central. …".
 */
export function minimapAriaLabel(o: { town: boolean; districtNames: readonly string[]; here: string; roadsTo: readonly string[]; joinedByRoad: boolean; caption: string }): string {
  const tail = `${o.caption}. Use the Directory to pick a place with the keyboard.`;
  if (o.town) return `Map of the whole town: ${o.districtNames.join(", ")}${o.joinedByRoad ? ", joined by road" : ""}. You are in ${o.here}. ${tail}`;
  const to = o.roadsTo.length ? `, with ${o.roadsTo.length === 1 ? "a road" : "roads"} to ${namesList(o.roadsTo)}` : "";
  return `Map of ${o.here}${to}. ${tail}`;
}

/** "A", "A and B", "A, B and C" (ADR-0044: Central has roads to three districts). */
function namesList(names: readonly string[]): string {
  return names.length <= 2 ? names.join(" and ") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** Hover text for a road to another district: "To Riverside (River Street): walk or drive along this road". */
export function roadLinkHover(link: Pick<RoadLink, "name" | "streetName">): string {
  return `${roadLinkLabel(link.name)}${link.streetName ? ` (${link.streetName})` : ""}: walk or drive along this road`;
}

// ---------------------------------------------------------------------------------------------
// District names (Town view)

export interface DistrictLabel {
  districtId: string;
  name: string;
  /** True for the visitor's district: outlined and labelled "you are here". */
  here: boolean;
  /** Centre of the name (canvas px); "you are here" goes on the line below when `here`. */
  x: number;
  y: number;
  /** The district's rectangle on the canvas (its outline). */
  box: { x: number; y: number; w: number; h: number };
}

/** "you are here" under the current district's name. */
export const YOU_ARE_HERE = "you are here";

/**
 * Each district's name in the half of its rectangle away from its square (where visitors arrive and "Return to plaza"
 * puts them), so the name never hides the "you" marker at spawn: near the bottom when the square is in the upper half
 * (Central, Riverside, Hillcrest), at the top when it is in the lower half (ADR-0044: the Sports Park's square is by
 * its south edge, where Arena Avenue comes in; the name may start 2 px above the rectangle, in the map's padding).
 * Kept inside the canvas.
 */
export function layoutDistrictLabels(
  v: MinimapView,
  districts: readonly Pick<District, "id" | "name" | "bounds" | "spawn">[],
  currentId: string | null | undefined,
  measure: (text: string) => number = (t) => approxTextWidth(t, 10),
  fontPx = 10,
): DistrictLabel[] {
  return districts.map((d) => {
    const box = rectToMinimap(v, d.bounds);
    const here = d.id === currentId;
    const w = Math.max(measure(d.name), here ? measure(YOU_ARE_HERE) : 0);
    const half = w / 2 + 3;
    const cx = Math.min(Math.max(box.x + box.w / 2, half), v.width - half);
    // The name's centre line; "you are here" is drawn one line (fontPx + 1.5 px) below it.
    const lines = here ? fontPx * 2 + 2 : fontPx;
    const squareLow = d.spawn.position.z < (d.bounds.minZ + d.bounds.maxZ) / 2;
    const y = squareLow
      ? Math.max(box.y + (fontPx + 1) / 2 - 2, fontPx / 2 + 2)
      : Math.max(Math.min(box.y + box.h - lines, v.height - lines), fontPx / 2 + 2);
    return { districtId: d.id, name: d.name, here, x: cx, y, box };
  });
}

// ---------------------------------------------------------------------------------------------
// "Where am I" caption

function distanceToRect(r: Aabb, x: number, z: number): number {
  const dx = Math.max(r.minX - x, 0, x - r.maxX);
  const dz = Math.max(r.minZ - z, 0, z - r.maxZ);
  return Math.hypot(dx, dz);
}

/** "On Elm Lane", "At 110 Main Street", or "In Townsy" (plain text for the caption and the screen-reader label). */
export function describePosition(
  pos: WorldPoint | null,
  plots: readonly MinimapPlot[],
  streets: readonly StreetInfo[] = WORLD_STREETS,
  maxStreetDistance = 12,
): string {
  if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.z)) return "Finding you…";
  let street: string | null = null;
  let best = Infinity;
  for (const s of streets) {
    for (const r of s.roads) {
      const d = distanceToRect(r, pos.x, pos.z);
      if (d < best) {
        best = d;
        street = s.name;
      }
    }
  }
  if (street && best <= 0.01) return `On ${street}`;
  const plot = plots.find((p) => distanceToRect(p.bounds, pos.x, pos.z) <= 0.01);
  if (plot) return `At ${plot.address}`;
  if (street && best <= maxStreetDistance) return `Near ${street}`;
  return "In Townsy";
}

/**
 * The caption under the map (world v5): where you are, with your district ("You: On River Street, Riverside";
 * "You: in Riverside" away from every street; "Finding you…" before the first position).
 */
export function minimapCaption(where: string, districtName: string): string {
  if (where === "Finding you…" || !districtName) return where === "Finding you…" ? where : `You: ${where}`;
  if (where === "In Townsy") return `You: in ${districtName}`;
  return `You: ${where}, ${districtName}`;
}

// ---------------------------------------------------------------------------------------------
// Per-viewer preference (on / off), remembered in localStorage when it is available.

export const MINIMAP_STORAGE_KEY = "townsy.minimap.v1";
/** Narrow screens (phones) start with the map closed; the HUD toggle opens it. */
export const MINIMAP_NARROW_MAX_WIDTH = 900;

export type StorageGetter = () => Pick<Storage, "getItem" | "setItem"> | null | undefined;

/** `window.localStorage` (merely reading the property throws when site data is blocked). */
export const browserLocalStorage: StorageGetter = () => (typeof window === "undefined" ? null : window.localStorage);

export function defaultMinimapOpen(viewportWidth: number | null | undefined): boolean {
  return !(typeof viewportWidth === "number" && viewportWidth <= MINIMAP_NARROW_MAX_WIDTH);
}

/** The saved choice, or `fallback` when nothing is saved or storage is unavailable / throws. */
export function readMinimapOpen(getStorage: StorageGetter, fallback: boolean): boolean {
  try {
    const raw = getStorage()?.getItem(MINIMAP_STORAGE_KEY);
    if (raw === "1") return true;
    if (raw === "0") return false;
  } catch {
    /* storage blocked: use the default */
  }
  return fallback;
}

/** Remember the choice; returns false when it could not be saved (the toggle still works this visit). */
export function writeMinimapOpen(getStorage: StorageGetter, open: boolean): boolean {
  try {
    const store = getStorage();
    if (!store) return false;
    store.setItem(MINIMAP_STORAGE_KEY, open ? "1" : "0");
    return true;
  } catch {
    return false;
  }
}

/** World v5: the Here / Town choice, remembered per viewer the same way (a convenience; "here" when unknown). */
export const MINIMAP_VIEW_STORAGE_KEY = "townsy.minimap.view.v1";

export function readMinimapView(getStorage: StorageGetter, fallback: MinimapViewKind = "here"): MinimapViewKind {
  try {
    const raw = getStorage()?.getItem(MINIMAP_VIEW_STORAGE_KEY);
    if (raw === "here" || raw === "town") return raw;
  } catch {
    /* storage blocked: use the default */
  }
  return fallback;
}

export function writeMinimapView(getStorage: StorageGetter, view: MinimapViewKind): boolean {
  try {
    const store = getStorage();
    if (!store) return false;
    store.setItem(MINIMAP_VIEW_STORAGE_KEY, view);
    return true;
  } catch {
    return false;
  }
}
